/** ======[README]======================================================================================================== */

/* 采集的信息种类：
    访问页面：【case1: 菜单 >>> 通过路由导航进入的页面】【case2: 菜单下的子页面 >>> 通过点击触发进入，路由地址未改变】
            路由事件 >>> routeIntoPage
            点击事件 >>> clickIntoPage
            其他事件 >>> 预留扩展
    访问功能：点击事件 >>> trickClick
*/

/* 数据结构模版:
    在各种采集信息的情况下，传递给后端的必要信息
    >>> 各种情况下，传递的数据结构保持一致
    >>> 每种情况下，包含各自的必要信息，尽可能的少
    >>> 需好配置、易扩展、好维护 >>> 尽可能少的修改已有的代码逻辑!!!
*/
const trackInfo = {
  eventType: 'RouteIntoPage', // 'RouteIntoPage', 'ClickIntoPage', 'TrickClick', 'xxEventType'
  targetId: '',
  refData: {
    targetName: '',
    pageName: '',
    pagePath: '',
  },

}


/* 前端配置
    在组件中按需引入埋点方法：
        import { inject } from "vue";
        const trackEvent = inject('trackEvent'); 
        const trackEventTypes = inject('trackEventTypes');

    1、访问菜单页面
        >>> 已有代码无需额外配置，该部分埋点数据采集，已经在插件中集成。

    2、访问菜单页面下的子页面
        >>> 在非菜单管理的子页面时，添加以下语句: >>> 无需改动已有代码逻辑。
            const trackData = { targetId: '', targetName: '', refData: {}, }
            trackEvent(trackEventTypes.clickIntoPage, trackData); // 通过插件方法采集埋点数据。

    3、重点功能采集埋点数据
        >>> 点击类功能：>>> 无需改动已有代码逻辑。
            <el-button v-track-click="trackData">重点功能</el-button>
            const trackData = { targetId: '', targetName: '', refData: {}, }
            
        >>> 非点击类功能：>>> 无需改动已有代码逻辑。
            const trackData = { targetId: '', targetName: '', refData: {}, }
            trackEvent(trackEventTypes.xxEventType, trackData); // 通过插件方法采集埋点数据。

*/




/** ======[插件：主体方法&必要数据]======================================================================================================== */
import { httpBus } from "@/api/index.js";
import { getToken } from "@/utils/auth";

export default {
  install(app, { router }) {
    registerTracking(app); // 注册公共的埋点方法|属性|指令
    trackRouteIntoPage(router); // 路由埋点
  }
};

/** 存储可用于埋点的事件类型 */
const eventTypes = {
  routeIntoPage: 'RouteIntoPage',
  clickIntoPage: 'ClickIntoPage',
  trickClick: 'TrickClick',
  create: 'Create',
  edit: 'Edit',
  search: 'Search',
  otherOperate: 'OtherOperate',
}

/** 存储当前页面信息 */
let currentPageInfo = {
  pageId: '',
  pageName: '',
  pagePath: '',
}

/** 埋点白名单路由 - 这些路由不需要发送埋点 */
const trackWhiteList = ['/login', '/auth-redirect', '/bind', '/register', '/401', '/404'];


/** ======[辅助：业务方法]======================================================================================================== */

/**
 * 注册采集埋点数据的方法&可用的事件类型
 * @param {*} app 
 */
function registerTracking(app) {
  // 1、注册全局方法 >>> 方便【选项式 API】使用
  app.config.globalProperties.$trackEvent = (eventType, data) => {
    // 发送数据到后端
    commitTrackInfo(eventType, data);
  };
  app.config.globalProperties.$trackEventTypes = eventTypes;


  // 2、注册provide方法 >>> 方便【组合式 API】使用
  app.provide('trackEvent', (eventType, data) => {
    const userRefData = data.refData;
    const trackInfo = {
      targetId: data?.targetId ?? '',
      refData: Object.assign({}, userRefData, {
        pageName: currentPageInfo.pageName,
        pagePath: currentPageInfo.pagePath,
      })
    };
    // 发送数据到后端
    commitTrackInfo(eventType, trackInfo);
  });
  app.provide('trackEventTypes', eventTypes);


  // 3、注册埋点指令: track-click
  registerTrackClickDirective(app);

}

/**
 * 路由埋点
 * @param {*} router 
 */
function trackRouteIntoPage(router) {
  // 初始化 currentPageInfo 
  currentPageInfo.pageName = router?.currentRoute?.value?.meta?.title ?? '';
  currentPageInfo.pagePath = router?.currentRoute?.value?.fullPath ?? '';

  router.afterEach((to, from) => {
    // 检查是否在白名单中
    if (trackWhiteList.includes(to.path)) {
      return;
    }
    
    // 检查是否有token，没有token不发送埋点
    if (!getToken()) {
      return;
    }
    
    const data = {
      targetId: '',
      refData: {
        targetName: 'RouteIntoPage',
        pageName: to?.meta?.title ?? '',
        pagePath: to?.fullPath ?? '',
      },
    };
    updateCurrentPageInfo(data.targetId, data.refData.pageName, data.refData.pagePath);
    commitTrackInfo(eventTypes.routeIntoPage, data);
  });
}


/**
 * 注册用于追踪click事件的埋点指令
 * @param {*} app 
 */
function registerTrackClickDirective(app) {
  const directiveOption = {
    mounted(el, binding) {
      el.addEventListener('click', () => {
        const userRefData = binding?.value?.refData ?? {};
        const data = {
          targetId: binding.value?.targetId ?? '',
          refData: Object.assign({}, userRefData, {
            pageName: currentPageInfo.pageName,
            pagePath: currentPageInfo.pagePath,
          })
        }
        commitTrackInfo(eventTypes.trickClick, data);
      });
    }
  };
  // 全局注册该指令
  app.directive('track-click', directiveOption);
}





/** ======[辅助：功能方法]======================================================================================================== */
/**
 * 检查是否可以发送埋点
 * @returns {boolean} 如果可以发送返回true，否则返回false
 */
function canCommitTrack() {
  // 检查是否有token
  if (!getToken()) {
    return false;
  }
  
  // 检查当前路由是否在白名单中
  const currentPath = currentPageInfo.pagePath || window.location.pathname;
  if (trackWhiteList.some(path => currentPath.startsWith(path))) {
    return false;
  }
  
  return true;
}

/**
 * 提交埋点信息
 * @param {*} eventType : click 
 * @param {*} data 
 */
function commitTrackInfo(eventType, data) {
  // 检查是否可以发送埋点
  if (!canCommitTrack()) {
    return;
  }
  
  const trackInfo = Object.assign({ eventType }, data)
  httpBus.request("post", `/schedule/job/browse/records/push`, { data: trackInfo }).then(res => {
  }).catch(err => {
    // 如果是401错误，静默处理，不打印错误（避免触发登录弹框）
    if (err?.response?.status === 401 || err?.message?.includes('401')) {
      return;
    }
    console.error('commit track info failed', err);
  });
}


/**
 * 更新当前所在的页面信息
 * @param {*} id 
 * @param {*} name 
 * @param {*} path 
 */
function updateCurrentPageInfo(id, name, path) {
  currentPageInfo.pageId = id;
  currentPageInfo.pageName = name;
  currentPageInfo.pagePath = path;
}

